/* oxlint-disable typescript/no-explicit-any */
import { ComponentClass, FunctionComponent, useEffect, useMemo } from 'react';

import { SiteControlLayout } from '@scaleaq/sitecontrol-lib';
import { v4 as uuidv4 } from 'uuid';

import { StaticPageNames } from '@/constants';
import { useLayoutDBSync, WidgetDescriptor } from '@/hooks';
import { useSiteContext } from '@/providers';
import { useLayoutStorage } from '@/stores';

import {
  getAddLayoutRequestFromSiteControlLayout,
  getDefaultNewLayout,
  getLayoutUpdateRequestFromSiteControlLayout,
  getNewWidget,
} from './helpFunctions/index.ts';

interface BaseAction {
  layoutId: string;
  widgetId: string;
  Component: ComponentClass<any> | FunctionComponent<any>;
  defaultPropsConfig: unknown;
  componentName: string;
}

interface NoAction extends Partial<BaseAction> {
  action?: undefined;
}

interface UpdateAction extends BaseAction {
  action: {
    type: 'update';
    dbLayout: SiteControlLayout;
    storeLayout?: SiteControlLayout;
    needsWidget: boolean;
  };
}

interface CreateAction extends BaseAction {
  action: {
    type: 'create';
    newLayout: SiteControlLayout;
    storeLayout?: SiteControlLayout;
  };
}

type UpdateValuesType = NoAction | UpdateAction | CreateAction;

type WidgetLayoutInfo = {
  layoutId: string;
  widgetId: string;
  Component: ComponentClass<any> | FunctionComponent<any>;
};

type UseUpdateProps<WDs extends readonly WidgetDescriptor<any, any>[]> = {
  pageName: StaticPageNames;
  widgets: WDs;
  source: string;
};

export function useUpdateFromDbAndGetStorageLayoutIds<
  WDs extends readonly WidgetDescriptor<any, any>[],
>({ widgets, pageName, source }: UseUpdateProps<WDs>): Record<string, WidgetLayoutInfo> {
  const { currentSite } = useSiteContext();
  const { allDbLayouts, updateLayoutInDb, addLayoutToDb } = useLayoutDBSync();
  const addLayoutToStorage = useLayoutStorage(s => s.addLayoutToStorage);
  const updateLayoutInStorage = useLayoutStorage(s => s.updateLayoutInStorage);
  const findStoredStaticLayoutByPageName = useLayoutStorage(
    s => s.findStoredStaticLayoutByPageName
  );

  // 1. Get stable IDs for each widget
  const updateValues = useMemo(() => {
    if (!allDbLayouts) return {};

    let dbLayout = allDbLayouts.find(l => l.pageName === pageName);
    const storeLayout = findStoredStaticLayoutByPageName(pageName);

    const result: Record<string, UpdateValuesType> = {};

    for (const { Component, defaultPropsConfig } of widgets) {
      const componentName = Component.name;

      if (dbLayout) {
        const widgetsInLayout = dbLayout.widgets.filter(w => !Object.keys(result).includes(w.id));
        let widgetId = widgetsInLayout.find(w => w.componentName === componentName)?.id;

        let needsWidget = false;

        if (!widgetId) {
          widgetId = uuidv4();
          needsWidget = true;
        }

        result[widgetId] = {
          layoutId: dbLayout.id,
          widgetId,
          Component: Component,
          defaultPropsConfig,
          componentName,
          action: {
            type: 'update',
            dbLayout,
            storeLayout,
            needsWidget,
          } as const,
        };
      } else if (currentSite) {
        // TODO: check if a new layout actually needs to be saved to the database and set in local storage
        dbLayout = getDefaultNewLayout(pageName);
        const newWidget = getNewWidget({
          componentName: componentName,
          defaultWidgetConfig: defaultPropsConfig as object,
          layoutId: dbLayout.id,
          siteId: currentSite.id,
        });
        dbLayout.widgets.push(newWidget);

        result[newWidget.id] = {
          layoutId: dbLayout.id,
          widgetId: newWidget.id,
          defaultPropsConfig,
          componentName,
          Component,
          action: {
            type: 'create',
            newLayout: dbLayout,
            storeLayout,
          } as const,
        };
      }
    }

    return result;
  }, [allDbLayouts, findStoredStaticLayoutByPageName, pageName, widgets, currentSite]);

  // 2
  useEffect(() => {
    Object.values(updateValues).forEach(update => {
      if (!update.action) return;

      switch (update.action.type) {
        case 'update': {
          const {
            defaultPropsConfig,
            componentName,
            action: { dbLayout, storeLayout, needsWidget },
          } = update;
          if (needsWidget) {
            if (currentSite) {
              dbLayout.widgets.push(
                getNewWidget({
                  componentName: componentName,
                  defaultWidgetConfig: defaultPropsConfig as object,
                  layoutId: dbLayout.id,
                  siteId: currentSite.id,
                })
              );
            }
            updateLayoutInDb(getLayoutUpdateRequestFromSiteControlLayout(dbLayout)).then(
              response => {
                if (storeLayout) {
                  updateLayoutInStorage(response);
                } else {
                  addLayoutToStorage(response);
                }
              }
            );
          }
          break;
        }
        case 'create': {
          const { newLayout, storeLayout } = update.action;
          const addLayoutRequest = getAddLayoutRequestFromSiteControlLayout({
            siteControlLayout: newLayout,
            source,
          });
          addLayoutToDb(addLayoutRequest).then(response => {
            if (storeLayout) {
              updateLayoutInStorage(response);
            } else {
              addLayoutToStorage(response);
            }
          });
          break;
        }
      }
    });
  }, [
    updateValues,
    addLayoutToStorage,
    updateLayoutInStorage,
    updateLayoutInDb,
    addLayoutToDb,
    source,
    currentSite,
  ]);

  return useMemo(() => {
    const map: Record<string, WidgetLayoutInfo> = {};
    for (const [key, val] of Object.entries(updateValues)) {
      if (val.layoutId && val.widgetId && val.Component) {
        map[key] = { layoutId: val.layoutId, widgetId: val.widgetId, Component: val.Component };
      }
    }
    return map;
  }, [updateValues]);
}
